import { useState, useEffect, useCallback } from 'react';
import { messagingService } from '../api/services/messaging.service';
import { SITE_ID, SUBDOMAIN } from '../assets/meta/meta';

// Custom hook for managing messaging state
export const useMessaging = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleRequest = useCallback(async (request) => {
    try {
      setLoading(true);
      setError(null);
      const result = await request();
      return result;
    } catch (err) {
      setError(err.message || 'An error occurred');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    clearError,
    handleRequest,
  };
};

// Hook for message operations
export const useMessages = (initialParams = {}) => {
  const [messages, setMessages] = useState([]);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
  });
  const { loading, error, clearError, handleRequest } = useMessaging();

  const fetchMessages = useCallback(async (params = {}) => {
    const mergedParams = { ...initialParams, ...params };
    const response = await handleRequest(() => messagingService.getMessages(mergedParams));
    if (response) {
      setMessages(response.data.results);
      setPagination({
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    }
    return response;
  }, [initialParams, handleRequest]);

  const sendAlimTalk = useCallback(async (data) => {
    return await handleRequest(() => messagingService.sendAlimTalk(data));
  }, [handleRequest]);

  const sendFriendTalk = useCallback(async (data) => {
    return await handleRequest(() => messagingService.sendFriendTalk(data));
  }, [handleRequest]);

  const sendSMS = useCallback(async (data) => {
    return await handleRequest(() => messagingService.sendSMS(data));
  }, [handleRequest]);

  const checkMessageStatus = useCallback(async (messageId) => {
    return await handleRequest(() => messagingService.checkMessageStatus(messageId));
  }, [handleRequest]);

  const getMessageHistory = useCallback(async (phoneNumber, siteId) => {
    return await handleRequest(() => messagingService.getMessageHistoryForPhone(phoneNumber, siteId));
  }, [handleRequest]);

  useEffect(() => {
      fetchMessages();
  }, []);

  return {
    messages,
    pagination,
    loading,
    error,
    clearError,
    fetchMessages,
    sendAlimTalk,
    sendFriendTalk,
    sendSMS,
    checkMessageStatus,
    getMessageHistory,
  };
};

// Hook for sender operations
export const useSenders = (initialParams = {}) => {
  const [senders, setSenders] = useState([]);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
  });
  const { loading, error, clearError, handleRequest } = useMessaging();

  const fetchSenders = useCallback(async (params = {}) => {
    const mergedParams = { ...initialParams, ...params };
    const response = await handleRequest(() => messagingService.getSenders(mergedParams));
    if (response) {
      setSenders(response.data.results);
      setPagination({
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    }
    return response;
  }, [initialParams, handleRequest]);

  const createSender = useCallback(async (data) => {
    return await handleRequest(() => messagingService.createSender(data));
  }, [handleRequest]);

  const updateSender = useCallback(async (id, data) => {
    return await handleRequest(() => messagingService.updateSenderManagement(id, data));
  }, [handleRequest]);

  const syncSenderStatus = useCallback(async (id) => {
    return await handleRequest(() => messagingService.syncSenderStatus(id));
  }, [handleRequest]);

  const getActiveSendersForSite = useCallback(async (siteId) => {
    return await handleRequest(() => messagingService.getActiveSendersForSite(siteId));
  }, [handleRequest]);

  useEffect(() => {
      fetchSenders();
  }, []);

  return {
    senders,
    pagination,
    loading,
    error,
    clearError,
    fetchSenders,
    createSender,
    updateSender,
    syncSenderStatus,
    getActiveSendersForSite,
  };
};

// Hook for template operations
export const useTemplates = (initialParams = {}) => {
  const [templates, setTemplates] = useState([]);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
  });
  const { loading, error, clearError, handleRequest } = useMessaging();

  const fetchTemplates = useCallback(async (params = {}) => {
    const mergedParams = { ...initialParams, ...params };
    const response = await handleRequest(() => messagingService.getTemplates(mergedParams));
    if (response) {
      setTemplates(response.data.results);
      setPagination({
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    }
    return response;
  }, [initialParams, handleRequest]);

  const createTemplate = useCallback(async (data) => {
    return await handleRequest(() => messagingService.createTemplate(data));
  }, [handleRequest]);

  const updateTemplate = useCallback(async (id, data) => {
    return await handleRequest(() => messagingService.updateTemplateManagement(id, data));
  }, [handleRequest]);

  const deleteTemplate = useCallback(async (id) => {
    return await handleRequest(() => messagingService.deleteTemplateManagement(id));
  }, [handleRequest]);

  const getApprovedTemplatesForSite = useCallback(async (siteId) => {
    return await handleRequest(() => messagingService.getApprovedTemplatesForSite(siteId));
  }, [handleRequest]);

  const getTemplatesForSender = useCallback(async (senderId) => {
    return await handleRequest(() => messagingService.getTemplatesForSender(senderId));
  }, [handleRequest]);

  const uploadImage = useCallback(async (file, siteId) => {
    return await handleRequest(() => messagingService.uploadImage(file, siteId, 'alimtalk'));
  }, [handleRequest]);

  const searchTemplates = useCallback(async (searchTerm, siteId) => {
    return await handleRequest(() => messagingService.searchTemplates(searchTerm, siteId));
  }, [handleRequest]);

  useEffect(() => {
      fetchTemplates();
  }, []);

  return {
    templates,
    pagination,
    loading,
    error,
    clearError,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getApprovedTemplatesForSite,
    getTemplatesForSender,
    searchTemplates,
    uploadImage,
  };
};

// Hook for site validation and configuration
export const useSiteValidation = () => {
  const [siteStatuses, setSiteStatuses] = useState([]);
  const [statistics, setStatistics] = useState({
    total_sites: 0,
    valid_sites: 0,
    sites_with_kakao_keys: 0,
    setup_completion: '0%',
  });
  const { loading, error, clearError, handleRequest } = useMessaging();

  const validateSite = useCallback(async (data) => {
    return await handleRequest(() => messagingService.validateSite(data));
  }, [handleRequest]);

  const getSiteStatus = useCallback(async () => {
    const response = await handleRequest(() => messagingService.getSiteStatus());
    if (response) {
      setSiteStatuses(response.data.sites);
      setStatistics(response.data.statistics);
    }
    return response;
  }, [handleRequest]);

  return {
    siteStatuses,
    statistics,
    loading,
    error,
    clearError,
    validateSite,
    getSiteStatus,
  };
};

// Hook for message sending form management
export const useMessageForm = (initialType = 'alimtalk') => {
  const [messageType, setMessageType] = useState(initialType);
  const [formData, setFormData] = useState({
    site_id: '',
    template_id: '',
    phone_number: '',
    message: '',
    template_params: {},
    buttons: [],
    fall_back_yn: 'N',
    fall_back_message: '',
    ad_flag: 'N',
  });
  const [selectedSite, setSelectedSite] = useState({
    id: SITE_ID, subdomain: SUBDOMAIN, kakaoChannelsId: ''
  });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const { loading, error, clearError, handleRequest } = useMessaging();

  const updateFormData = useCallback((updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData(prev => ({
      site_id: prev.site_id,
      template_id: '',
      phone_number: '',
      message: '',
      template_params: {},
      buttons: [],
      fall_back_yn: 'N',
      fall_back_message: '',
      ad_flag: 'N',
    }));
    // setSelectedSite(null);
    // setSelectedTemplate(null);
  }, []);

  const sendMessage = useCallback(async () => {
    let response;
    console.log('Sending message with data:', { messageType, formData });
    switch (messageType) {
      case 'alimtalk':
        response = await handleRequest(() => messagingService.sendAlimTalk(formData));
        break;
      case 'friendtalk':
        response = await handleRequest(() => messagingService.sendFriendTalk(formData));
        break;
      case 'sms':
        response = await handleRequest(() => messagingService.sendSMS(formData));
        break;
      default:
        throw new Error('Invalid message type');
    }
    return response;
  }, [messageType, formData, handleRequest]);

  return {
    messageType,
    setMessageType,
    formData,
    updateFormData,
    selectedSite,
    setSelectedSite,
    selectedTemplate,
    setSelectedTemplate,
    loading,
    error,
    clearError,
    resetForm,
    sendMessage,
  };
};

// Hook for real-time message status tracking
export const useMessageTracking = (messageIds = []) => {
  const [messageStatuses, setMessageStatuses] = useState({});
  const { handleRequest } = useMessaging();

  const trackMessage = useCallback(async (messageId) => {
    try {
      const response = await handleRequest(() => messagingService.checkMessageStatus(messageId));
      if (response) {
        setMessageStatuses(prev => ({
          ...prev,
          [messageId]: response.data,
        }));
      }
      return response;
    } catch (error) {
      console.error(`Failed to track message ${messageId}:`, error);
    }
  }, [handleRequest]);

  const trackMultipleMessages = useCallback(async (ids) => {
    const promises = ids.map(id => trackMessage(id));
    return await Promise.allSettled(promises);
  }, [trackMessage]);

  useEffect(() => {
    if (messageIds.length > 0) {
      trackMultipleMessages(messageIds);
    }
  }, [messageIds]);

  return {
    messageStatuses,
    trackMessage,
    trackMultipleMessages,
  };
};

// Hook for bulk operations
export const useBulkMessaging = () => {
  const [bulkOperations, setBulkOperations] = useState([]);
  const [progress, setProgress] = useState({ completed: 0, total: 0, errors: [] });
  const { loading, error, clearError, handleRequest } = useMessaging();

  const sendBulkMessages = useCallback(async (recipients, messageData) => {
    setProgress({ completed: 0, total: recipients.length, errors: [] });
    const results = [];
    
    for (let i = 0; i < recipients.length; i++) {
      const recipient = recipients[i];
      try {
        const data = {
          ...messageData,
          phone_number: recipient.phone_number,
          message: messageData.message.replace(/\{(\w+)\}/g, (match, key) => {
            return recipient[key] || match;
          }),
        };
        
        let response;
        switch (messageData.type) {
          case 'alimtalk':
            response = await messagingService.sendAlimTalk(data);
            break;
          case 'friendtalk':
            response = await messagingService.sendFriendTalk(data);
            break;
          case 'sms':
            response = await messagingService.sendSMS(data);
            break;
          default:
            throw new Error('Invalid message type');
        }
        
        results.push({ recipient, response: response.data, success: true });
      } catch (error) {
        results.push({ recipient, error: error.message, success: false });
        setProgress(prev => ({
          ...prev,
          errors: [...prev.errors, { recipient, error: error.message }],
        }));
      }
      
      setProgress(prev => ({ ...prev, completed: i + 1 }));
    }
    
    return results;
  }, []);

  const resetProgress = useCallback(() => {
    setProgress({ completed: 0, total: 0, errors: [] });
  }, []);

  return {
    bulkOperations,
    progress,
    loading,
    error,
    clearError,
    sendBulkMessages,
    resetProgress,
  };
};